import { useEffect, useState } from "react";

import { calculateOffsetLeft, calculateOffsetTop } from "../utils/offset";
import { RANKIFIER_ROOT_ID } from "../constants";
import { RankifierCardType } from "../contexts/rankifier";
import { RenderableDestinations } from "../contexts/rankifier/rankifierDestinations";
import { useRankifierDestinations } from "./useRankifierDestinations";
import { useRankifierState } from "./useRankifierState";

export type Position = {
  x: number;
  y: number;
  width: number;
  height: number;
  index: number;
};

export type CardPath = {
  [key in RenderableDestinations]?: HTMLDivElement;
};

export type CardPositions = {
  [key in RenderableDestinations]?: Position;
};

export const useRankifierCardPosition = (
  card?: RankifierCardType,
): {
  [key in RenderableDestinations]?: Position;
} => {
  const { refs: destinations, resizeClock } = useRankifierDestinations();
  const [{ cards }] = useRankifierState();
  const { deck, hand, selected } = cards;
  const [cardPath, setCardPath] = useState<CardPath>({});
  const [cardPositions, setCardPositions] = useState<CardPositions>({});

  const updateCardPath = (destination: RenderableDestinations) => {
    if (cardPath[destination]) {
      return;
    }
    const destinationCards = cards[destination];
    const index = destinationCards.findIndex((c) => c?.index === card?.index);
    if (index !== -1) {
      const ref = destinations[destination].ref;
      const element = ref.current;
      if (!element) {
        return;
      }
      const destinationEl = element.querySelector(`#destination-${destination}-${index}`);
      if (!destinationEl) {
        return;
      }

      setCardPath((prev) => ({
        ...prev,
        [destination]: destinationEl,
      }));
    }
  };

  const countPosition = (destination: RenderableDestinations) => {
    const pathElement = cardPath[destination];
    if (!pathElement) {
      return;
    }
    const { width, height } = pathElement.getBoundingClientRect();

    const top = calculateOffsetTop(pathElement, RANKIFIER_ROOT_ID);
    const left = calculateOffsetLeft(pathElement, RANKIFIER_ROOT_ID);

    setCardPositions((prev) => ({
      ...prev,
      [destination]: {
        x: left,
        y: top,
        width,
        height,
        index: pathElement.id?.split("-")[2] || 0,
      },
    }));
  };

  useEffect(() => updateCardPath("deck"), [deck]);
  useEffect(() => updateCardPath("hand"), [hand]);
  useEffect(() => updateCardPath("selected"), [selected]);

  useEffect(() => countPosition("deck"), [cardPath.deck, resizeClock]);
  useEffect(() => countPosition("hand"), [cardPath.hand, resizeClock]);
  useEffect(() => countPosition("selected"), [cardPath.selected, resizeClock]);

  return cardPositions;
};
